
import {finalize} from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService, DialogService } from '../ui';
import { Dossier } from '../dossier/dossier';
import { CapitalisationService, CapitalisationInput, CapitalisationRecordInput } from './capitalisation.service';

import { each, map, trim, every, find } from 'lodash';

import { FileUploader, FileItem, ParsedResponseHeaders } from 'ng2-file-upload';
import { RecordService } from '../record/record.service';
import { Record } from '../record/record';
import { RecordSearchDialogComponent } from '../record/record-search-dialog.component';
import { DossierModel, Target } from '../dossier-model/dossier-model';
import { RecordLink } from '../record-link/record-link';


export interface GroupData {
  pp: number
  np: number
  u: number
  isEmpty?: boolean
}

export interface RecordData {
  record?: Record
  name: string
  groups: GroupData[]
  isEmpty?: boolean
}

export interface CapitalisationMatrix {
  groups: string[]
  records: RecordData[]
}


@Component({
  templateUrl: './capitalisation.component.html',
})
export class CapitalisationComponent implements OnInit {

  title: string
  dossier: Dossier
  dossierModel: DossierModel

  isFileOver: boolean = false
  uploader: FileUploader

  rootRecordId: string

  matrix: CapitalisationMatrix

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notifier: NotifierService,
    private capitalisationService: CapitalisationService,
    private recordService: RecordService,
    private dialogService: DialogService
  ){}


  initForm() {
    // Init uploader
    this.uploader = new FileUploader({
      url: '/files/capitalisation',
      autoUpload: true
    });

    this.uploader.onSuccessItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      let result = JSON.parse(response);

      if (result.success) {
        this.matrix = prepareMatrix(result.matrix);
      }
      else {
        if (result.message) {
          this.notifier.error(result.message);

          this.matrix = null;
        }
      }
    };

    this.uploader.onBeforeUploadItem = (fileItem: FileItem) => {
      this.notifier.updateProgress(0);

      return { fileItem };
    }

    this.uploader.onProgressItem = (fileItem: FileItem, progress: any) => {
      this.notifier.updateProgress(progress);

      return {
        fileItem,
        progress
      }
    }

    this.uploader.onCompleteAll = () => {
      this.notifier.endProgress();
    };
  }


  ngOnInit() {
    this.title = this.route.routeConfig.data.title;

    // Get the data
    this.notifier.startProgress();

    this.route.data
    .subscribe((data) => {
      this.dossier = data.dossier;
      this.dossierModel = data.dossierModel;

      // Get the root record
      const rootTarget = find<Target>(this.dossierModel.targets, target => target.link.is_root);
      const rootLink = find(this.dossier.links, l => l.name === rootTarget.id) as RecordLink;

      if(rootLink) {
        this.rootRecordId = rootLink.record.id;
      }
      
      this.initForm();

      this.notifier.endProgress();
    });
  }


  get canSave():Boolean {
    if(!this.matrix) {
      return false;
    }

    if(!this.matrix.records) {
      return false;
    }

    if(this.matrix.records.length === 0) {
      return false;
    }

    if(every(this.matrix.records, record => record.isEmpty)) {
      return false;
    }

    return true;
  }


  save() {
    if(!this.canSave) {
      return;
    }

    let input = matrixToInput(this.matrix);

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    this.capitalisationService
      .importCapitalisationMatrix(this.dossier.id, input).pipe(
      finalize(onComplete))
      .subscribe((execution) => {
        this.notifier.success('Table de capitalisation importée avec succès');

        this.router.navigate(['dossiers', this.dossier.id]);
      });
  }

  cancel() {
    if(this.matrix) {
      this.matrix = null;
    }
    else {
      this.router.navigate(['dossiers', this.dossier.id]);
    }
  }

  linkRecord(recordData: RecordData) {
    this.dialogService.show({
      component: RecordSearchDialogComponent,
      props: {

        search: trim(recordData.name),
        hideId: this.rootRecordId,

        onClose: () => {
          this.dialogService.hide();
        },

        onSelect: (record: Record) => {
          this.dialogService.hide();

          this.recordService
            .record(record.id).pipe(
            finalize(() => {
              this.notifier.endProgress();
            }))
            .subscribe((record) => {
              recordData.record = record;
            });
        }
      }
    });

    event.preventDefault();
  }

  unlinkRecord(recordData: RecordData) {
    recordData.record = null;
  }
}

function isGroupEmpty(group: GroupData) {
  return group.pp === 0 && group.np === 0 && group.u === 0;
}

function prepareMatrix(matrix: CapitalisationMatrix) {
  each(matrix.records, record => {
    record.isEmpty = true;

    each(record.groups, group => {
      group.isEmpty = isGroupEmpty(group);
      record.isEmpty = record.isEmpty && group.isEmpty; // If all groups are empty then the record will be empty (= true)
    });
  });

  return matrix;
}

function matrixToInput(matrix: CapitalisationMatrix): CapitalisationInput {
  // Create initial records
  let records: CapitalisationRecordInput[] = map(matrix.records, record => ({
    id: record.record ? record.record.id : null,
    name: record.name,
    links: map(record.groups,(g,index) => ({
      group_index: index,
      pp: g.pp,
      np: g.np,
      u: g.u,
    })).filter(g => !isGroupEmpty(g))
  })).filter(r => r.links.length > 0);

  return {
    groups: matrix.groups,
    records,
  };
}